<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<template>

  <div
    :class="{
      notification: true,
      unviewed: !props.props.viewed,
      clickable,
    }"
    @click="openNotification"
    data-testid="notification-card"
  >
    <div class="notification-header">
      <h2
        v-if="props.type && props.type !== 'facilitator_pending'"
        :class="{
          [getNotificationClass]: true,
          title: true,
        }"
        data-testid="notification-title"
      >
        {{ props.type ? props.type.split('_').join(' ') : '' }}
      </h2>
      <p class="notification-time" data-testid="notification-time">
        {{ formattedTime }}
      </p>
    </div>

    <div class="notification-subheader">
      <h3 class="subtitle" data-testid="notification-subtitle">
        {{ props.props.subtitle }}
      </h3>
    </div>

    <div class="notification-info">
      <img
        :src="getImageUrl()"
        alt="notificationIcon"
        data-testid="notification-icon"
      />
      <p data-testid="notification-info-text">
        {{ props.props.info }}
      </p>

      <div
        class="moreActions"
        @click.stop="toggleReadModal"
        data-xpath="notification-more-actions"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
          <path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
        </svg>
      </div>

      <ReadModal
        v-if="isReadModalShow"
        v-click-outside="closeReadModal"
        v-bind="{
          id: props.props.id,
          closeModal: closeReadModal,
        }"
        data-testid="notification-read-modal"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, state } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import moment from 'moment';
import constants from '@/constants';
import { jobStatusTypes } from '@/constants/jobFilter';
import ReadModal from './ReadModal.vue';

const props = defineProps({
	props: { type: Object, required: true },
});

const store = useStore();
const router = useRouter();
const isReadModalShow = ref(false);

const clickable = computed(() => !!actionRoute.value);

const actionRoute = computed(() => {
	let action;

	if (constants.notificationActions.userDetails.includes(props.props.type)) {
		action = '/users/settings';
	} else if (
		constants.notificationActions.forApprove.includes(props.props.type)
        || !props.props.externalId
	) {
		if (
			store.state.currentUserRole === 'BANK-OFFICER-MANAGER'
          || store.state.currentUserRole === 'ADMIN'
		) {
			action = '/rightToWorkRequests/pending';
		}
	} else if (constants.notificationActions.jobDetails.includes(props.props.type)) {
		action = `/jobs/${props.props.externalId}/details`;
	} else if (constants.notificationActions.jobRates.includes(props.props.type)) {
		action = `/jobs/${props.props.externalId}/rates`;
	} else if (constants.notificationActions.messageDetails.includes(props.props.type)) {
		action = `/messages/${props.props.chatRoomId}`;
	}

	return action;
});

const formattedTime = computed(() => moment(props.props.time).format('HH:mm'));

const getNotificationClass = computed(() => {
	if (constants.notifications.newImage.includes(props.props.type)) {
		return 'newNotification';
	}
	if (constants.notifications.okImage.includes(props.props.type)) {
		return 'confirmedNotification';
	}
	return 'cancelNotification';
});

function getImageUrl() {
	let pic;
	if (constants.notifications.newImage.includes(props.props.type)) {
		pic = 'new';
	} else if (constants.notifications.okImage.includes(props.props.type)) {
		pic = 'ok';
	} else {
		pic = 'cancel';
	}
	return require(`../../../assets/${pic}.svg`);
}

function toggleReadModal() {
	isReadModalShow.value = !isReadModalShow.value;
}

function closeReadModal() {

	isReadModalShow.value = false;
}

function openNotification() {
	const data = {
		id: props.props.id,
		viewed: true,
	};

	store
		.dispatch('readNotification', data)
		.then(() => {
			store.dispatch('loadNotifications');
			store.dispatch('loadNotificationsCount');
		});
	if (actionRoute.value) {
		router.push({ path: actionRoute.value });
	}

	if (props.props.closeModal) {
		props.props.closeModal();
	}

	if (constants.notificationActions.forApprove.includes(props.props.type)) {
		store.dispatch('setJobsFilter', {
			...store.state.jobsFilter,
			status: jobStatusTypes.READY_FOR_APPROVE,
		});
	}
}
</script>

<style scoped lang="scss">
.notification {
  border-bottom: 1px solid #eff1f5;

  &-header {
    display: flex;
    justify-content: space-between;
    padding: 15px 18px 0 39px;
    font-weight: 600;

    .newNotification {
      color: #ea3ad4;
    }

    .confirmedNotification {
      color: #52b8f6;
    }

    .cancelNotification {
      color: #f54e4e;
    }

    .title {
      text-transform: uppercase;
      font-size: 15px;
      margin: 0;
      font-weight: 600;
    }
  }

  &-time {
    color: #96b0d7;
    font-size: 12px;
    margin: 0;
  }

  &-subheader {
    padding-left: 39px;
    font-size: 14px;

    .subtitle {
      margin: 0;
      color: #051376;
      font-size: 14px;
      font-weight: 600;
    }
  }

  &-info {
    position: relative;
    display: flex;
    justify-content: space-between;
    padding: 0 18px 0 39px;
    font-size: 11px;

    img {
      position: absolute;
      left: 13px;
      top: -7px;
    }

    p {
      width: 90%;
      font-size: 14px;
    }

    .moreActions {
      cursor: pointer;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.unviewed {
  background-color: #eff8ff;
  font-weight: 700;
}

.clickable {
  cursor: pointer;
}
</style>
